import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, brl, formatDate } from "../../lib/api";
import { computeInstallments, installmentsSum, defaultFirstDue, nextDueDateForDay, toISODate } from "../../lib/installments";
import { toast } from "sonner";
import { Trash2, Plus, ChevronLeft, ChevronRight, Check, User, ShoppingBag, ClipboardCheck, Wallet } from "lucide-react";
import ApprovalDecision from "../../components/ApprovalDecision";
import BRLInput from "../../components/BRLInput";
import ProductPicker from "../../components/ProductPicker";
import { useAuth } from "../../contexts/AuthContext";

const STEPS = [
  { n: 1, label: "Cliente", icon: User },
  { n: 2, label: "Produtos", icon: ShoppingBag },
  { n: 3, label: "Condições", icon: Wallet },
  { n: 4, label: "Confirmação", icon: ClipboardCheck },
];

export default function NewSale() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [products, setProducts] = useState([{ name: "", qty: 1, price: 0 }]);
  const [pricePolicy, setPricePolicy] = useState("all");
  const [installments, setInstallments] = useState(2);
  const [firstDue, setFirstDue] = useState(defaultFirstDue());
  const [method, setMethod] = useState("pix");
  const [downPayment, setDownPayment] = useState(0);
  const [saleDate, setSaleDate] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; });
  const [discount, setDiscount] = useState(0);
  const [surcharge, setSurcharge] = useState(0);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState(null);
  const [step, setStep] = useState(1);
  const [confirmSale, setConfirmSale] = useState(false);

  useEffect(() => { (async () => setClients((await api.get("/clients")).data))(); }, []);
  useEffect(() => { (async () => { try { setPricePolicy((await api.get("/products/sale-settings")).data.price_change_policy || "all"); } catch { /* ignore */ } })(); }, []);

  const canEditPrice = pricePolicy === "all" || (pricePolicy === "admin_only" && user?.role === "admin");

  const addFromCatalog = (prod) => {
    const existing = products.find((p) => p.product_id === prod.id);
    const newQty = existing ? Number(existing.qty || 1) + 1 : 1;
    setProducts((prev) => {
      const idx = prev.findIndex((p) => p.product_id === prod.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Number(next[idx].qty || 1) + 1 };
        return next;
      }
      const row = { name: prod.nome_produto, qty: 1, price: Number(prod.valor_venda) || 0,
        product_id: prod.id, barcode: prod.codigo_barras || "", codigo_interno: prod.codigo_interno || "", catalog_price: Number(prod.valor_venda) || 0, price_reason: "" };
      const empty = prev.findIndex((p) => !p.name && !p.product_id);
      if (empty >= 0) { const next = [...prev]; next[empty] = row; return next; }
      return [...prev, row];
    });
    return newQty;
  };

  const subtotal = products.reduce((a, p) => a + Number(p.qty || 0) * Number(p.price || 0), 0);
  const total = Math.max(0, subtotal - Number(discount || 0) + Number(surcharge || 0));
  const client = clients.find((c) => c.id === clientId);
  const available = client ? Math.max(0, Number(client.credit_limit) - Number(client.open_balance)) : 0;
  const financed = Math.max(0, total - Number(downPayment));

  const installmentRows = computeInstallments(financed, installments, firstDue);
  const installmentsTotal = installmentsSum(installmentRows);
  const installmentsMatch = Math.abs(installmentsTotal - Math.round(financed * 100) / 100) < 0.005;

  useEffect(() => {
    if (!clientId || total <= 0) { setDecision(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/intelligence/preflight", {
          client_id: clientId, total, down_payment: Number(downPayment) || 0, installments: Number(installments),
        });
        setDecision(data);
      } catch { setDecision(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [clientId, total, downPayment, installments]);

  const setProduct = (i, k, v) => {
    const p = [...products]; p[i] = { ...p[i], [k]: v }; setProducts(p);
  };

  const openConfirmSale = () => {
    if (!clientId) { toast.error("Selecione um cliente"); return; }
    if (total <= 0) { toast.error("Adicione produtos"); return; }
    if (financed > 0 && !installmentsMatch) { toast.error("A soma das parcelas difere do valor a financiar. Revise a simulacao."); return; }
    if (decision?.decision === "DENIED") { toast.error(decision.reason); return; }
    if (decision?.decision === "REQUIRES_DOWNPAYMENT" && Number(downPayment) < decision.suggested_down_payment) {
      toast.error(`Entrada minima de R$ ${decision.suggested_down_payment.toFixed(2)} obrigatoria`);
      return;
    }
    setConfirmSale(true);
  };

  const createSale = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        client_id: clientId,
        products: products.map(p => ({ name: p.name, qty: Number(p.qty), price: Number(p.price), product_id: p.product_id || undefined, barcode: p.barcode || undefined, price_reason: p.price_reason || undefined })),
        installments: Number(installments),
        payment_method: method,
        down_payment: Number(downPayment),
        first_due_date: firstDue || undefined,
        sale_date: saleDate || undefined,
        discount: Number(discount) || 0,
        surcharge: Number(surcharge) || 0,
        notes: notes || "",
        opportunity_id: params.get("opportunity_id") || undefined,
      };
      const { data } = await api.post("/sales", payload);
      setConfirmSale(false);
      if (params.get("opportunity_id")) {
        toast.success(`Venda #${data.number} criada. Oportunidade marcada como convertida.`);
      } else {
        toast.success(`Venda #${data.number} criada. Envie o link de assinatura.`);
      }
      nav(`/vendas/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar venda");
    } finally { setSubmitting(false); }
  };

  const goNext = () => {
    if (step === 1 && !clientId) { toast.error("Selecione um cliente"); return; }
    if (step === 2 && total <= 0) { toast.error("Adicione produtos"); return; }
    if (step === 3) {
      if (financed > 0 && !installmentsMatch) { toast.error("A soma das parcelas difere do valor a financiar. Revise a simulacao."); return; }
      if (decision?.decision === "DENIED") { toast.error(decision.reason); return; }
      if (decision?.decision === "REQUIRES_DOWNPAYMENT" && Number(downPayment) < decision.suggested_down_payment) {
        toast.error(`Entrada minima de R$ ${decision.suggested_down_payment.toFixed(2)} obrigatoria`); return;
      }
    }
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div data-testid="new-sale-page" className="space-y-6">
      <div className="topbar">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-brand">Vendas</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-brand-dark">Nova Venda</h1>
        </div>
      </div>

      {/* Progress bar */}
      <div className="rounded-2xl border border-border bg-white p-4 shadow-card sm:p-5" data-testid="sale-step-indicator">
        <div className="flex items-center">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const done = step > s.n;
            const active = step === s.n;
            return (
              <React.Fragment key={s.n}>
                <div className="flex flex-1 flex-col items-center gap-1.5 sm:flex-row sm:flex-none sm:gap-2">
                  <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold transition-colors ${
                    active ? "bg-brand-grad text-white shadow-[0_4px_12px_rgba(106,27,154,0.3)]" : done ? "bg-brand text-white" : "bg-muted text-[#9A8A98]"
                  }`}>
                    {done ? <Check size={16} /> : <Icon size={16} />}
                  </span>
                  <span className={`text-xs font-semibold ${active || done ? "text-brand-dark" : "text-[#9A8A98]"}`}>{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`mx-2 hidden h-0.5 flex-1 rounded sm:block ${step > s.n ? "bg-brand" : "bg-muted"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Resumo compacto (mobile) */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-white to-[#FFF7FC] p-4 shadow-card lg:hidden" data-testid="sale-summary-mobile">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Cliente</div>
            <div className="truncate text-sm font-medium text-brand-dark">{client?.name || "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Total</div>
            <div className="tabular text-sm font-bold text-brand-dark">{brl(total)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">A financiar</div>
            <div className="tabular text-sm font-bold text-brand">{brl(financed)}</div>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault(); }} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* STEP 1 — Cliente */}
          {step === 1 && (
            <section className="rounded-2xl border border-border bg-white p-5 shadow-card sm:p-6 animate-fade-in">
              <h2 className="section-title mb-6">Cliente</h2>
              {params.get("interesse") ? (
                <div className="mb-4 rounded-xl border border-brand/20 bg-brand/5 px-3 py-2 text-sm text-brand-dark" data-testid="sale-interest-ref">
                  <span className="font-semibold">Interesse informado:</span> “{params.get("interesse")}”
                </div>
              ) : null}
              <select className="input-underline" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="sale-client-select">
                <option value="">Selecione...</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{`${c.name} · score ${c.score} · limite ${brl(c.credit_limit)}`}</option>)}
              </select>
              <div className="mt-4">
                <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Data da venda</label>
                <input type="date" className="input-underline" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} data-testid="sale-date-input" />
              </div>
              {client && (
                <div className="mt-6 grid grid-cols-3 gap-3 rounded-2xl bg-muted/60 p-4 text-center sm:gap-6">
                  <div><div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Score</div><div className="mt-1 text-lg font-bold text-brand-dark sm:text-2xl">{client.score}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Limite</div><div className="mt-1 text-lg font-bold text-brand-dark sm:text-2xl">{brl(client.credit_limit)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Disponivel</div><div className={`mt-1 text-lg font-bold sm:text-2xl ${financed > available ? "text-red-500" : "text-brand"}`}>{brl(available)}</div></div>
                </div>
              )}
            </section>
          )}

          {/* STEP 2 — Produtos */}
          {step === 2 && (
            <>
              <section className="rounded-2xl border border-border bg-white p-5 shadow-card sm:p-6 animate-fade-in">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="section-title">Produtos</h2>
                  <button type="button" onClick={() => setProducts([...products, { name: "", qty: 1, price: 0 }])} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-brand hover:underline" data-testid="add-product-btn"><Plus size={14} /> Adicionar manual</button>
                </div>
                <ProductPicker onPick={addFromCatalog} testidPrefix="sale-picker" />
                <div className="space-y-5">
                  {products.map((p, i) => (
                    <div key={i} className="rounded-2xl border border-border bg-muted/50 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Item #{i + 1}</div>
                        {products.length > 1 && (
                          <button type="button" onClick={() => setProducts(products.filter((_, k) => k !== i))} className="text-red-500 hover:text-red-700" data-testid={`remove-product-${i}`}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-12">
                        <div className="col-span-2 md:col-span-7">
                          <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Nome do produto *</label>
                          <input className="input-underline w-full" placeholder="Ex: Conjunto rendado P" value={p.name} onChange={(e) => setProduct(i, "name", e.target.value)} readOnly={!!p.product_id} data-testid={`product-name-${i}`} />
                          {p.product_id && <div className="mt-1 text-[10px] text-[#7A6A78]">{p.codigo_interno ? `${p.codigo_interno} · ` : ""}Catálogo{p.barcode ? ` · cód. ${p.barcode}` : ""}</div>}
                        </div>
                        <div className="col-span-1 md:col-span-2">
                          <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Qtd *</label>
                          <input className="input-underline tabular w-full" type="number" min="1" inputMode="numeric" value={p.qty} onChange={(e) => setProduct(i, "qty", e.target.value)} data-testid={`product-qty-${i}`} />
                        </div>
                        <div className="col-span-1 md:col-span-3">
                          <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Valor unitario (R$) *</label>
                          <BRLInput value={p.price} onChange={(v) => setProduct(i, "price", v)} disabled={!!p.product_id && !canEditPrice} testid={`product-price-${i}`} />
                        </div>
                      </div>
                      {p.product_id && p.catalog_price != null && Math.abs(Number(p.price) - Number(p.catalog_price)) > 0.005 && (
                        <div className="mt-3">
                          <label className="mb-1 block text-[10px] uppercase tracking-widest text-red-500">Motivo da alteração de preço (catálogo: {brl(p.catalog_price)})</label>
                          <input className="input-underline w-full" value={p.price_reason || ""} onChange={(e) => setProduct(i, "price_reason", e.target.value)} placeholder="Descreva o motivo" data-testid={`product-price-reason-${i}`} />
                        </div>
                      )}
                      {Number(p.qty) > 0 && Number(p.price) > 0 && (
                        <div className="mt-3 flex justify-between border-t border-border pt-3 text-sm">
                          <span className="text-[#7A6A78]">Subtotal</span>
                          <span className="tabular font-medium text-brand-dark">{brl(Number(p.qty) * Number(p.price))}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-2xl border border-border bg-white p-5 shadow-card sm:p-6">
                <h2 className="section-title mb-4">Observacoes</h2>
                <textarea rows={3} className="w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" placeholder="Observacoes sobre a venda (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sale-notes-input" />
              </section>
            </>
          )}

          {/* STEP 3 — Condições da venda (condições + simulação + decisão) */}
          {step === 3 && (
            <section className="rounded-2xl border border-border bg-white p-5 shadow-card sm:p-6 animate-fade-in" data-testid="sale-conditions-step">
              <h2 className="section-title mb-6">Condições da venda</h2>
              <div className="mb-5 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-muted/60 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Valor total</div>
                  <div className="tabular mt-1 text-xl font-bold text-brand-dark" data-testid="conditions-total">{brl(total)}</div>
                </div>
                <div className="rounded-2xl bg-brand/5 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Valor a financiar</div>
                  <div className="tabular mt-1 text-xl font-bold text-brand" data-testid="conditions-financed">{brl(financed)}</div>
                </div>
              </div>
              <div className="space-y-4 text-sm">
                <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex min-w-0 flex-col"><span className="mb-1 text-[10px] uppercase tracking-widest text-[#7A6A78]">Desconto</span><BRLInput value={discount} onChange={setDiscount} testid="sale-discount-input" /></div>
                  <div className="flex min-w-0 flex-col"><span className="mb-1 text-[10px] uppercase tracking-widest text-[#7A6A78]">Acrescimo</span><BRLInput value={surcharge} onChange={setSurcharge} testid="sale-surcharge-input" /></div>
                  <div className="flex min-w-0 flex-col"><span className="mb-1 text-[10px] uppercase tracking-widest text-[#7A6A78]">Entrada</span><BRLInput value={downPayment} onChange={setDownPayment} testid="down-payment-input" /></div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Parcelas</label>
                    <select className="input-underline" value={installments} onChange={(e) => setInstallments(e.target.value)} data-testid="sale-installments-select">
                      {[1,2,3,4,5,6,8,10,12].map(n => <option key={n} value={n}>{`${n}x de ${brl(financed / n)}`}</option>)}
                    </select>
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">1º vencimento</label>
                    <input type="date" className="input-underline" value={firstDue} onChange={(e) => setFirstDue(e.target.value)} data-testid="sale-first-due-input" />
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Vencer todo dia</label>
                    <select className="input-underline" value="" onChange={(e) => { if (e.target.value) setFirstDue(nextDueDateForDay(e.target.value)); }} data-testid="sale-due-day-select">
                      <option value="">Escolher dia…</option>
                      {[5,10,15,20,25].map((d) => <option key={d} value={d}>{`Dia ${d}`}</option>)}
                    </select>
                  </div>
                  <div className="min-w-0 sm:col-span-2">
                    <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#7A6A78]">Forma de pagamento</label>
                    <select className="input-underline" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="sale-method-select">
                      <option value="pix">PIX</option>
                      <option value="cash">Dinheiro</option>
                      <option value="card">Cartao</option>
                      <option value="link">Link de pagamento</option>
                      <option value="boleto">Boleto</option>
                    </select>
                  </div>
                </div>

                {financed > 0 && (
                  <div className="border-t border-border pt-4" data-testid="installments-simulation">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Simulacao das parcelas</span>
                      <span className="text-[10px] uppercase tracking-widest text-[#7A6A78]">{installments}x</span>
                    </div>
                    <div className="space-y-1.5">
                      {installmentRows.map((r) => (
                        <div key={r.number} className="flex items-center justify-between text-sm" data-testid={`installment-row-${r.number}`}>
                          <span className="text-[#7A6A78]">Parcela {r.number} <span className="text-[10px]">· venc. {formatDate(toISODate(r.dueDate))}</span></span>
                          <span className="tabular font-medium text-brand-dark" data-testid={`installment-amount-${r.number}`}>{brl(r.amount)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-sm">
                      <span className="font-medium">Total das parcelas</span>
                      <span className={`tabular font-bold ${installmentsMatch ? "text-emerald-600" : "text-red-500"}`} data-testid="installments-total">{brl(installmentsTotal)}</span>
                    </div>
                    {!installmentsMatch && (
                      <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2 text-[11px] text-red-700" data-testid="installments-mismatch-warning">
                        A soma das parcelas ({brl(installmentsTotal)}) difere do valor a financiar ({brl(financed)}).
                      </div>
                    )}
                  </div>
                )}

                {client && financed > available && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                    Limite excedido. Disponivel: {brl(available)}
                  </div>
                )}
                {decision && <div><ApprovalDecision data={decision} /></div>}
              </div>
            </section>
          )}

          {/* STEP 4 — Confirmação (revisão read-only antes de registrar) */}
          {step === 4 && (
            <section className="rounded-2xl border border-border bg-white p-5 shadow-card sm:p-6 animate-fade-in" data-testid="sale-confirm-step">
              <h2 className="section-title mb-6">Confirmação da venda</h2>

              <div className="mb-5 flex items-center justify-between rounded-2xl bg-muted/60 p-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Cliente</div>
                  <div className="truncate text-sm font-semibold text-brand-dark">{client?.name || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#7A6A78]">Data da venda</div>
                  <div className="text-sm font-medium text-brand-dark">{saleDate ? formatDate(saleDate) : "—"}</div>
                </div>
              </div>

              <div className="mb-5">
                <div className="mb-2 text-[10px] uppercase tracking-widest text-[#7A6A78]">Produtos</div>
                <div className="space-y-1.5">
                  {products.filter((p) => p.name).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm" data-testid={`confirm-product-${i}`}>
                      <span className="min-w-0 truncate text-brand-dark">{p.qty}x {p.name}</span>
                      <span className="tabular ml-3 shrink-0 font-medium text-brand-dark">{brl(Number(p.qty) * Number(p.price))}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2 border-t border-border pt-4 text-sm">
                <div className="flex justify-between"><span className="text-[#7A6A78]">Subtotal</span><span className="tabular font-medium">{brl(subtotal)}</span></div>
                {Number(discount) > 0 && <div className="flex justify-between"><span className="text-[#7A6A78]">Desconto</span><span className="tabular text-emerald-600">- {brl(discount)}</span></div>}
                {Number(surcharge) > 0 && <div className="flex justify-between"><span className="text-[#7A6A78]">Acréscimo</span><span className="tabular">+ {brl(surcharge)}</span></div>}
                <div className="flex justify-between font-semibold text-brand-dark"><span>Valor total</span><span className="tabular" data-testid="confirm-total">{brl(total)}</span></div>
                {Number(downPayment) > 0 && <div className="flex justify-between"><span className="text-[#7A6A78]">Entrada</span><span className="tabular">- {brl(downPayment)}</span></div>}
                <div className="flex justify-between rounded-xl bg-brand/5 px-3 py-2"><span className="font-medium text-brand-dark">A financiar</span><span className="tabular font-bold text-brand" data-testid="confirm-financed">{brl(financed)}</span></div>
                <div className="flex justify-between pt-1"><span className="text-[#7A6A78]">Parcelamento</span><span className="font-medium text-brand-dark">{installments}x · 1º venc. {formatDate(firstDue)}</span></div>
                <div className="flex justify-between"><span className="text-[#7A6A78]">Forma de pagamento</span><span className="font-medium text-brand-dark">{method.toUpperCase()}</span></div>
              </div>

              {financed > 0 && (
                <div className="mt-4 border-t border-border pt-4" data-testid="confirm-installments-simulation">
                  <div className="mb-2 text-[10px] uppercase tracking-widest text-[#7A6A78]">Simulação das parcelas</div>
                  <div className="space-y-1.5">
                    {installmentRows.map((r) => (
                      <div key={r.number} className="flex items-center justify-between text-sm" data-testid={`confirm-installment-row-${r.number}`}>
                        <span className="text-[#7A6A78]">Parcela {r.number} <span className="text-[10px]">· venc. {formatDate(toISODate(r.dueDate))}</span></span>
                        <span className="tabular font-medium text-brand-dark">{brl(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-sm">
                    <span className="font-medium">Total das parcelas</span>
                    <span className={`tabular font-bold ${installmentsMatch ? "text-emerald-600" : "text-red-500"}`}>{brl(installmentsTotal)}</span>
                  </div>
                  {!installmentsMatch && (
                    <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">
                      A soma das parcelas ({brl(installmentsTotal)}) difere do valor a financiar ({brl(financed)}). Volte e revise.
                    </div>
                  )}
                </div>
              )}

              {decision && <div className="mt-4"><ApprovalDecision data={decision} /></div>}
            </section>
          )}

          {/* Navegação entre etapas */}
          <div className="flex items-center justify-between gap-3">
            {step > 1 ? (
              <button type="button" onClick={goBack} className="brand-outline" data-testid="step-back-btn"><ChevronLeft size={16} /> Voltar</button>
            ) : <span />}
            {step < 4 ? (
              <button type="button" onClick={goNext} className="brand" data-testid="step-next-btn">Continuar <ChevronRight size={16} /></button>
            ) : (
              <button type="button" onClick={openConfirmSale} className="brand" disabled={submitting || !client || !installmentsMatch || decision?.decision === "DENIED"} data-testid="submit-sale-btn">
                <Check size={16} /> Registrar Venda
              </button>
            )}
          </div>
        </div>

        {/* Resumo lateral (desktop) */}
        <aside className="hidden lg:block">
          <section className="sticky top-6 rounded-2xl border border-border bg-white p-5 shadow-card sm:p-6" data-testid="sale-summary">
            <h2 className="section-title mb-5">Resumo da venda</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#7A6A78]">Cliente</span><span className="max-w-[55%] truncate font-medium text-brand-dark">{client?.name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-[#7A6A78]">Itens</span><span className="font-medium text-brand-dark">{products.filter((p) => p.name).length}</span></div>
              <div className="flex justify-between"><span className="text-[#7A6A78]">Subtotal</span><span className="tabular font-medium">{brl(subtotal)}</span></div>
              {Number(discount) > 0 && <div className="flex justify-between"><span className="text-[#7A6A78]">Desconto</span><span className="tabular text-emerald-600">- {brl(discount)}</span></div>}
              {Number(surcharge) > 0 && <div className="flex justify-between"><span className="text-[#7A6A78]">Acréscimo</span><span className="tabular">+ {brl(surcharge)}</span></div>}
              <div className="flex justify-between border-t border-border pt-3"><span>Total</span><span className="tabular font-semibold text-brand-dark" data-testid="sale-total">{brl(total)}</span></div>
              {Number(downPayment) > 0 && <div className="flex justify-between"><span className="text-[#7A6A78]">Entrada</span><span className="tabular">- {brl(downPayment)}</span></div>}
              <div className="flex justify-between rounded-xl bg-brand/5 px-3 py-2"><span className="font-medium text-brand-dark">A financiar</span><span className="tabular font-bold text-brand">{brl(financed)}</span></div>
              {step >= 3 && financed > 0 && (
                <div className="text-xs text-[#7A6A78]">{installments}x de {brl(financed / installments)} · {method.toUpperCase()}</div>
              )}
            </div>
          </section>
        </aside>
      </form>

      {/* Segunda confirmação antes de registrar a venda */}
      {confirmSale && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-brand-dark/60 p-6" onClick={() => !submitting && setConfirmSale(false)}>
          <div className="max-h-[92vh] w-full max-w-sm overflow-y-auto rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="confirm-sale-modal">
            <div className="mb-4 flex flex-col items-center text-center">
              <span className="mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-brand-pink/10 text-brand"><ClipboardCheck size={26} /></span>
              <h2 className="text-lg font-bold text-brand-dark">Confirmar venda</h2>
              <p className="mt-1 text-sm text-[#7A6A78]">Você confirma o registro desta venda?</p>
            </div>

            <div className="space-y-2 rounded-2xl border border-border bg-muted/40 p-4 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Cliente</span>
                <span className="truncate font-semibold text-brand-dark">{client?.name || "—"}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Valor total</span>
                <span className="tabular font-semibold text-brand-dark" data-testid="confirm-modal-total">{brl(total)}</span>
              </div>
              {Number(downPayment) > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Entrada</span>
                  <span className="tabular font-medium text-brand-dark">{brl(downPayment)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Valor financiado</span>
                <span className="tabular text-lg font-bold text-brand" data-testid="confirm-modal-financed">{brl(financed)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Parcelas</span>
                <span className="font-medium text-brand-dark">{installments}x de {brl(financed / installments)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">1º vencimento</span>
                <span className="font-medium text-brand-dark">{formatDate(firstDue)}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Forma de pagamento</span>
                <span className="font-medium text-brand-dark">{method.toUpperCase()}</span>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <button onClick={createSale} disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-grad py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
                data-testid="confirm-sale-final-btn">
                <Check size={17} /> {submitting ? "Registrando..." : "Confirmar venda"}
              </button>
              <button onClick={() => setConfirmSale(false)} disabled={submitting}
                className="w-full rounded-xl py-2.5 text-xs font-semibold uppercase tracking-widest text-[#7A6A78] hover:bg-muted disabled:opacity-60"
                data-testid="confirm-sale-cancel-btn">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
